import { Link, NavLink, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
    LayoutDashboard, Users, FolderKanban, Settings, Plug, LogOut, Activity, Plus, Shield,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
    { to: "/projects", label: "Projects", icon: FolderKanban, testid: "nav-projects" },
    { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

/** UX Sprint 1 — entries surfaced only to admins, under an "Advanced" header. */
const ADMIN_NAV = [
    { to: "/integrations", label: "Future APIs", icon: Plug, testid: "nav-integrations" },
];

export default function AppLayout() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();

    return (
        <div className="min-h-screen flex items-start bg-[#050505] text-zinc-100">
            {/* Sidebar */}
            <aside className="hidden lg:flex w-64 border-r border-zinc-900 flex-col sticky top-0 h-screen bg-[#080808]" data-testid="sidebar">
                <div className="px-6 py-6 border-b border-zinc-900">
                    <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
                        <div className="w-8 h-8 bg-[#FF4400] grid place-items-center rounded-sm">
                            <span className="font-display font-bold text-sm text-white">F</span>
                        </div>
                        <div className="leading-tight">
                            <div className="font-display font-bold text-base">FURMAIS</div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-mono">Creative OS</div>
                        </div>
                    </Link>
                </div>
                <nav className="flex-1 py-4 px-3 space-y-1 scrollbar-thin overflow-y-auto">
                    {NAV.map(({ to, label, icon: Icon, testid }) => (
                        <NavLink
                            key={to}
                            to={to}
                            data-testid={testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                                    isActive
                                        ? "bg-zinc-900 text-white border border-zinc-800"
                                        : "text-zinc-400 hover:text-white hover:bg-zinc-900/60 border border-transparent"
                                }`
                            }
                        >
                            <Icon className="w-4 h-4" />
                            <span>{label}</span>
                        </NavLink>
                    ))}
                    {user?.is_admin && (
                        <>
                            <div className="pt-4 mt-4 border-t border-zinc-900">
                                <div className="px-3 mb-2 label-micro" data-testid="sidebar-admin-section">Admin</div>
                            </div>
                            <NavLink
                                to="/admin"
                                data-testid="nav-admin"
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                                        isActive
                                            ? "bg-[#FF4400]/10 text-[#FF4400] border border-[#FF4400]/40"
                                            : "text-[#FF4400]/80 hover:text-[#FF4400] hover:bg-[#FF4400]/5 border border-transparent"
                                    }`
                                }
                            >
                                <Shield className="w-4 h-4" />
                                <span>Admin</span>
                            </NavLink>
                            {ADMIN_NAV.map(({ to, label, icon: Icon, testid }) => (
                                <NavLink
                                    key={to}
                                    to={to}
                                    data-testid={testid}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                                            isActive
                                                ? "bg-zinc-900 text-white border border-zinc-800"
                                                : "text-zinc-500 hover:text-white hover:bg-zinc-900/60 border border-transparent"
                                        }`
                                    }
                                >
                                    <Icon className="w-4 h-4" />
                                    <span>{label}</span>
                                </NavLink>
                            ))}
                        </>
                    )}
                    <div className="pt-4 mt-4 border-t border-zinc-900">
                        <div className="px-3 mb-2 label-micro">Quick</div>
                        <button
                            data-testid="quick-new-project"
                            onClick={() => nav("/projects/new")}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm rounded-sm bg-white text-black hover:bg-zinc-200 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            New Project
                        </button>
                    </div>
                </nav>
                <div className="p-3 border-t border-zinc-900">
                    <div className="flex items-center gap-3 px-3 py-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-zinc-800 grid place-items-center text-xs font-display font-bold">
                            {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                            <div className="text-sm text-white truncate" data-testid="sidebar-user-name">{user?.name || "User"}</div>
                            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono">{user?.role || "owner"}</div>
                        </div>
                    </div>
                    <button
                        data-testid="logout-btn"
                        onClick={() => { logout(); nav("/login"); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-400 hover:text-white hover:bg-zinc-900 rounded-sm transition-colors"
                    >
                        <LogOut className="w-4 h-4" /> Logout
                    </button>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 min-w-0 flex flex-col">
                {/* Top bar */}
                <header className="border-b border-zinc-900 bg-[#080808]/80 backdrop-blur sticky top-0 z-10">
                    <div className="h-14 px-4 sm:px-6 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Link to="/dashboard" className="lg:hidden font-display font-bold">FURMAIS</Link>
                            <div className="hidden lg:flex items-center gap-2 text-[11px] font-mono text-zinc-500">
                                <Activity className="w-3 h-3 text-emerald-400 pulse-dot rounded-full" />
                                <span>SYSTEM ONLINE</span>
                                <span className="text-zinc-700">/</span>
                                <span className="text-zinc-400">{loc.pathname}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Link to="/projects/new" data-testid="topbar-new-project" className="btn-primary text-xs px-3 py-1.5 lg:hidden">
                                <Plus className="w-3 h-3" /> New
                            </Link>
                            <Link to="/clients/new" data-testid="topbar-new-client" className="btn-secondary text-xs px-3 py-1.5">
                                <Plus className="w-3 h-3" /> Client
                            </Link>
                        </div>
                    </div>
                </header>
                <div className="flex-1">
                    <Outlet />
                </div>
            </main>
            <Toaster theme="dark" />
        </div>
    );
}
